// AuthProvider - React context for authentication state

import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { getCurrentUser, login as loginApi, logout as logoutApi } from '@/services/auth'
import type { User, LoginCredentials, UserRole, Permission } from '@/types'
import { ROLE_PERMISSIONS } from '@/types/auth'

interface PermissionHelpers {
  canViewContent: boolean
  canEditContent: boolean
  canViewAssets: boolean
  canEditAssets: boolean
  canSchedule: boolean
  canDownload: boolean
  canManageUsers: boolean
  hasPermission: (permission: Permission) => boolean
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isAdmin: boolean
  isDesigner: boolean
  isUser: boolean
  role: UserRole | null
  permissions: PermissionHelpers
  isLoading: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

// Public routes that don't require authentication
const PUBLIC_ROUTES = ['/login', '/forgot-password', '/reset-password', '/share', '/guide']

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTES.some(route => pathname.startsWith(route))
}

/**
 * Validate returnTo URL to prevent open redirect attacks
 * Only allows relative paths that don't escape to external sites
 */
function validateReturnTo(url: string | null): string {
  if (!url) return '/'

  // Must start with / (relative path)
  if (!url.startsWith('/')) return '/'

  // Must not start with // (protocol-relative URL to external site)
  if (url.startsWith('//')) return '/'

  // Must not contain :// (absolute URL embedded in path)
  if (url.includes('://')) return '/'

  // Must not contain backslashes (potential bypass)
  if (url.includes('\\')) return '/'

  return url
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const currentUser = await getCurrentUser()
        setUser(currentUser)
        
        // If not authenticated and on protected route, redirect to login
        if (!currentUser && !isPublicRoute(location.pathname)) {
          // Store intended destination
          const returnTo = location.pathname + location.search
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true })
        }
      } catch (error) {
        console.error('Auth check failed:', error)
        setUser(null)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  const login = useCallback(async (credentials: LoginCredentials) => {
    const loggedInUser = await loginApi(credentials)
    setUser(loggedInUser)

    // Redirect to intended destination or home (validated to prevent open redirect)
    const params = new URLSearchParams(location.search)
    const returnTo = validateReturnTo(params.get('returnTo'))
    navigate(returnTo, { replace: true })
  }, [navigate, location.search])

  const logout = useCallback(async () => {
    await logoutApi()
    setUser(null)
    navigate('/login', { replace: true })
  }, [navigate])

  // Compute role and permissions
  const role: UserRole | null = user?.role || null
  const isAdmin = role === 'admin'
  const isDesigner = role === 'designer'
  const isUser = role === 'user'

  // Get permissions for current role (default to user if no role)
  const rolePermissions = role ? ROLE_PERMISSIONS[role] : ROLE_PERMISSIONS.user

  const permissions = useMemo<PermissionHelpers>(() => ({
    canViewContent: rolePermissions.canViewContent,
    canEditContent: rolePermissions.canEditContent,
    canViewAssets: rolePermissions.canViewAssets,
    canEditAssets: rolePermissions.canEditAssets,
    canSchedule: rolePermissions.canSchedule,
    canDownload: rolePermissions.canDownload,
    canManageUsers: rolePermissions.canManageUsers,
    hasPermission: (permission: Permission) => rolePermissions[permission],
  }), [rolePermissions])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isAdmin,
        isDesigner,
        isUser,
        role,
        permissions,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
